import { useContext, useMemo, useState } from 'react';
import Select from 'react-select';
import Stepper from './Stepper';
import { OETrans } from '@components/translation/OETrans';
import { Button, Modal, Row, Col } from 'react-bootstrap';
import { SectionTitle } from '@components/atomic/atoms';
import SectionSubtitle from '@components/atomic/atoms/SectionSubtitle';
import ChecklistCard from './ChecklistCards';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getInfratelTemplateConfigurations, getSmartGridTemplateConfiguration } from '@lib/api_ts/templates';
import { UserContext } from '@components/contexts/UserProvider';
import { IcoCheckmarkSolid, IcoClose, IcoLoadingSpinner, Trash } from '@icons';
import { generateTrackRecordTemplates } from '@lib/api_ts/trackRecords';
import FileUploadButton from './FileUploadButton';

const outputOptions = [
	{ label: 'Contratti e atti di attingimento', value: 'contracts' },
	{ label: 'Fatture', value: 'invoices' },
	{ label: 'Pagamenti', value: 'payments' },
	{ label: 'Timesheets', value: 'timesheets' },
];

export interface GenerateTrackRecordProps {
	showModalTrackRecord: boolean;
	entityName: string;
	entityId: string;
	cancelFn: () => void;
	entityType: 'projects' | 'projectaggregates';
	infratelSettings: boolean;
}

const GenerateTrackRecord = ({
	entityType,
	showModalTrackRecord,
	entityName,
	entityId,
	cancelFn,
	infratelSettings,
}: GenerateTrackRecordProps) => {
	const {
		myUser: { companies },
	} = useContext(UserContext);
	const [currentStep, setCurrentStep] = useState(0);

	const [trackRecordName, setTrackRecordName] = useState('');
	const [configurationId, setConfigurationId] = useState('');
	const [outputs, setOutputs] = useState<string[]>([]);
	const [sourceFile, setSourceFile] = useState<File>();
	const [projectSourceFiles, setProjectSourceFiles] = useState<{ [name: string]: File }>({});

	const updateProjectSourceFile = (name: string) => (file: File) => {
		setProjectSourceFiles((currFiles) => ({ ...currFiles, [name]: file }));
	};

	const { data: infratelConfigurationsData } = useQuery({
		queryKey: ['infratelConfigs'],
		queryFn: () => getInfratelTemplateConfigurations(),
		enabled: !!companies.length && (infratelSettings || entityType === 'projectaggregates'),
		placeholderData: [],
	});

	const { data: smartGridConfigurationData } = useQuery({
		queryKey: ['smartGridConfig'],
		queryFn: () => getSmartGridTemplateConfiguration(),
		enabled: entityType === 'projects' && !infratelSettings,
		placeholderData: [],
	});

	const configurationsData = useMemo(
		() => (infratelSettings ? infratelConfigurationsData : smartGridConfigurationData),
		[infratelSettings, infratelConfigurationsData, smartGridConfigurationData],
	);

	const configurationOptions = useMemo(
		() => configurationsData.map((config) => ({ value: config.id.toString(), label: config.label })),
		[configurationsData],
	);

	const selectedConfiguration = useMemo(
		() => configurationsData.find((config) => config.id.toString() === configurationId),
		[configurationId, configurationsData],
	);

	const isTemplateIdMandatory =
		entityType === 'projectaggregates' ||
		(entityType === 'projects' && ['DEFAULT', 'DEFAULT-5G'].includes(selectedConfiguration?.projectType));

	const mandatorySourceFile =
		entityType === 'projects' ? selectedConfiguration?.files.some((file) => projectSourceFiles?.[file?.code]) : sourceFile;

	const {
		data: generateTemplateRequestSent,
		isPending: generateTemplatePending,
		mutate: generateTemplate,
	} = useMutation({
		mutationFn: () =>
			generateTrackRecordTemplates(
				entityType,
				entityId,
				configurationId,
				outputs.join('|'),
				entityType === 'projects' ? Object.values(projectSourceFiles) : [sourceFile],
				entityType === 'projects'
					? selectedConfiguration.files.filter((val) => !!projectSourceFiles[val.code]).map((val) => val.code)
					: [selectedConfiguration.projectType],
				trackRecordName,
			),
		onSuccess: (_) => setCurrentStep(currentStep + 1),
	});

	const canGoToNextStep = useMemo(
		() => [
			!!configurationId && (!isTemplateIdMandatory || !!trackRecordName),
			!!outputs.length,
			!!mandatorySourceFile,
			!!generateTemplateRequestSent,
		],
		[trackRecordName, isTemplateIdMandatory, configurationId, outputs, mandatorySourceFile, generateTemplateRequestSent],
	);

	const recap = useMemo(
		() => [
			...(trackRecordName ? [{ label: 'Codice univoco', value: trackRecordName }] : []),
			{ label: 'Regole di compilazione', value: selectedConfiguration?.label ?? '' },
			{ label: 'Entità', value: outputs.map((output) => outputOptions.find((opt) => opt.value === output)?.label)?.join(', ') ?? '' },
			{
				label: 'File sorgente',
				value:
					entityType === 'projectaggregates'
						? sourceFile?.name
						: (Object.values(projectSourceFiles)
								?.map((val) => val.name)
								?.join(' - ') ?? ''),
			},
		],
		[trackRecordName, selectedConfiguration, sourceFile, outputs, projectSourceFiles, entityType],
	);

	const resetAll = () => {
		setTrackRecordName('');
		setConfigurationId('');
		setOutputs([]);
		setSourceFile(undefined);
		setCurrentStep(0);
		setProjectSourceFiles({});
	};

	return (
		<Modal
			show={showModalTrackRecord}
			onHide={() => cancelFn()}
			fullscreen
			centered
			className="creation-modal-aggregates"
			enforceFocus={false}
			autoFocus={false}
		>
			<Modal.Body className="overflow-hidden p-0">
				<Row className="h-100">
					<Col className="col-1 pt-5 offcanvas-modal-aggregates">
						<Stepper steps={['Codice e regole', 'Entità', 'File sorgente', 'Generazione zip']} activeIndex={currentStep} />
					</Col>

					<Col className="content-modal-aggregates" style={{ padding: '3rem 1.5rem 3rem 1.5rem' }}>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
							<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
								<h2 style={{ textTransform: 'none', fontWeight: 'bold', fontSize: 40 }}>Configurazione del file template</h2>
								<h3 style={{ fontSize: 28, fontWeight: 'bold' }}>
									{
										{
											projects: 'PROGETTO',
											projectaggregates: 'GRUPPO',
										}[entityType]
									}
									: {entityName}
								</h3>
							</div>

							<Button
								className="btn custom-ghost-button"
								onClick={cancelFn}
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									gap: 8,
									fontWeight: '500',
									paddingTop: 0,
								}}
							>
								<OETrans code="common.cancel" fallback={'Annulla'} />
								<IcoClose className="ico-path-fill-second-color" />
							</Button>
						</div>

						{[
							<div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
								<SectionTitle>Regole di compilazione</SectionTitle>
								<SectionSubtitle>
									Scegli dalla lista la regola di compilazione di cui hai bisogno, è utile alla generazione dei file corretti.
								</SectionSubtitle>

								<div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 600 }}>
									<label style={{ fontWeight: 500 }}>Regole di compilazione</label>
									<Select
										className="react-select-container"
										classNamePrefix="react-select"
										name="companyId"
										placeholder={'Scegli una regola di compilazione ...'}
										options={configurationOptions}
										onChange={(e) => setConfigurationId(e.value)}
										value={configurationOptions.find((config) => config.value === configurationId)}
									/>
								</div>
								<SectionTitle>Codice univoco</SectionTitle>
								<div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 600 }}>
									<label style={{ fontWeight: 500 }}>
										Codice identificativo ed univoco {!isTemplateIdMandatory && !!configurationId && <i>(facoltativo)</i>}
									</label>
									<input
										onChange={(e) => setTrackRecordName(e.target.value)}
										value={trackRecordName}
										disabled={!configurationId}
										maxLength={25}
										placeholder="Inserisci un codice identificativo ed univoco ..."
										style={{ width: 600, height: 40, padding: '11px 8px', borderRadius: 5, borderColor: '#C7CFD7' }}
									/>
									<span style={{ fontSize: 12, color: '#4A5968' }}>Massimo {trackRecordName.length}/25 caratteri alfanumerici</span>
								</div>
							</div>,
							<div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
								<SectionTitle>Entità</SectionTitle>
								<SectionSubtitle>Scegli la tipologia di dati da caricare in piattaforma. La scelta può essere multipla.</SectionSubtitle>
								<div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 48 }}>
									{outputOptions.map((outputOpt) => (
										<ChecklistCard
											key={outputOpt.value}
											label={outputOpt.label}
											checked={outputs.includes(outputOpt.value)}
											setChecked={(checked) =>
												checked
													? setOutputs((currOutputs) => [...currOutputs, outputOpt.value])
													: setOutputs((currOpts) => currOpts.filter((currOpt) => currOpt !== outputOpt.value))
											}
										/>
									))}
								</div>
							</div>,
							<>
								<div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
									<SectionTitle>File sorgente</SectionTitle>
									<SectionSubtitle>
										{
											{
												projects: 'Carica i file sorgente da associare al progetto.',
												projectaggregates: 'Carica il file sorgente da associare al gruppo.',
											}[entityType]
										}
									</SectionSubtitle>
									{
										{
											projects: (
												<>
													<div
														style={{
															display: 'flex',
															flexDirection: 'column',
															gap: 16,
															alignItems: 'flex-start',
															justifyContent: 'flex-start',
														}}
													>
														{selectedConfiguration?.files?.map((file) => (
															<div
																key={file.id}
																style={{
																	width: 780,
																	height: 96,
																	padding: 24,
																	display: 'flex',
																	justifyContent: 'space-between',
																	alignItems: 'center',
																	backgroundColor: 'white',
																	borderRadius: 8,
																	gap: 24,
																	fontWeight: 'bold',
																}}
															>
																<span style={{ fontSize: 18 }}>{file.label}</span>
																{projectSourceFiles?.[file.code] ? (
																	<div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 24 }}>
																		{projectSourceFiles?.[file.code]?.name}
																		<Button
																			className="btn custom-ghost-button"
																			style={{ color: '#CC0000', display: 'flex', gap: 12, alignItems: 'center' }}
																			onClick={() =>
																				setProjectSourceFiles((currVal) => {
																					const { [file.code]: _, ...newVal } = currVal;
																					return newVal;
																				})
																			}
																		>
																			Elimina
																			<Trash />
																		</Button>
																	</div>
																) : (
																	<FileUploadButton htmlKey={file.code} setterFn={updateProjectSourceFile(file.code)} />
																)}
															</div>
														))}
													</div>
												</>
											),
											projectaggregates: (
												<>
													{sourceFile && (
														<>
															<div
																style={{
																	width: 780,
																	height: 96,
																	padding: 24,
																	display: 'flex',
																	justifyContent: 'space-between',
																	alignItems: 'center',
																	backgroundColor: 'white',
																	borderRadius: 8,
																	gap: 24,
																	fontWeight: 'bold',
																}}
															>
																<span style={{ fontSize: 18 }}>{sourceFile.name}</span>
																<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
																	<span style={{ fontSize: 18, color: '#008055' }}>Caricamento completato</span>
																	<IcoCheckmarkSolid style={{ width: 24, height: 24 }} />
																</div>
															</div>
															Vai avanti oppure carica un file sostitutivo
														</>
													)}
													<FileUploadButton htmlKey="group" setterFn={setSourceFile} />
												</>
											),
										}[entityType]
									}
								</div>
							</>,
							<div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
								<SectionTitle>Riepilogo delle informazioni</SectionTitle>
								<div
									style={{
										width: 900,
										padding: 24,
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'space-between',
										alignItems: 'flex-start',
										// backgroundColor: '#DEE4F1',
										backgroundColor: 'white',
										borderRadius: 8,
										gap: 24,
										fontSize: 16,
									}}
								>
									{recap.map((recapField) => (
										<div key={recapField.label} style={{ display: 'flex' }}>
											<span style={{ width: 180, fontWeight: 'bold' }}>{recapField.label}: </span>
											<span style={{ fontWeight: 500 }}>{recapField?.value || ''}</span>
										</div>
									))}
								</div>
							</div>,
							<div>
								<SectionTitle>Generazione ZIP</SectionTitle>
								<div
									style={{
										width: 900,
										padding: 24,
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										backgroundColor: 'white',
										borderRadius: 8,
										fontSize: 16,
										margin: '40px 0',
									}}
								>
									{generateTemplatePending || generateTemplateRequestSent ? (
										<>
											<span style={{ fontWeight: 'bold' }}>L'elaborazione del tuo file è iniziata</span>
											<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 32 }}>
												<span style={{ fontWeight: 'bold', color: '#4400B3' }}>Elaborazione in corso...</span>
												<IcoLoadingSpinner width={48} height={48} style={{ animation: 'spin 2s linear infinite' }} />
											</div>
										</>
									) : (
										<>
											<span style={{ fontWeight: 'bold' }}>
												Genera un file zip contenente i template per il caricamento dei dati di progetto
											</span>
											<Button className="btn custom-primary-button" onClick={() => generateTemplate()}>
												Genera file zip
											</Button>
										</>
									)}
								</div>
								{generateTemplateRequestSent && (
									<SectionSubtitle>
										Questo processo richiederà del tempo (anche più di 10 minuti per lavorazioni complesse).
										<br /> Ecco cosa fare ora: <br />
										<br />
										&#8226; Puoi chiudere questa pagina <br />
										&#8226; Aspetta la nostra notifica <br />
										&#8226; Troverai il link per scaricare il file direttamente lì
									</SectionSubtitle>
								)}
							</div>,
						].at(currentStep)}
					</Col>
				</Row>
			</Modal.Body>
			<Modal.Footer className="footer-shadow justify-content-between">
				<Button
					className="btn custom-ghost-error-button d-flex align-items-center"
					onClick={resetAll}
					disabled={generateTemplateRequestSent || generateTemplatePending}
					style={{ gap: '0.5rem' }}
				>
					<Trash width={17.5} />
					Resetta Tutto
				</Button>
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16 }}>
					{currentStep > 0 && !generateTemplateRequestSent && (
						<Button className="btn custom-outline-button" onClick={() => setCurrentStep(currentStep - 1)} style={{ width: 270 }}>
							Indietro
						</Button>
					)}
					{currentStep < 3 && (
						<Button
							className="btn custom-primary-button"
							disabled={!canGoToNextStep[currentStep]}
							onClick={() => setCurrentStep(currentStep + 1)}
							style={{ width: 270 }}
						>
							Avanti
						</Button>
					)}
					{currentStep === 3 && (
						<Button className="btn custom-primary-button" onClick={() => generateTemplate()} style={{ width: 270 }}>
							Genera template
						</Button>
					)}
					{generateTemplateRequestSent && (
						<Button className="btn custom-primary-button" onClick={() => cancelFn()} style={{ width: 270 }}>
							Chiudi
						</Button>
					)}
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default GenerateTrackRecord;
